import { PublicHeaderWrapper } from "../../../_theme/layout/publicPagesComponent/HeaderWrapper";
import CustomFormInput from "../../modules/apps/components/CustomFormInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IRegistrationForm, IRegistrationDropdownData } from "../core/models";
import { useSnackbar } from "../../../_theme/context/SnackbarContext";
import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import CustomFormSearchSelect from "../../modules/apps/components/CustomFormSearchSelect";
import { getRegistrationDropdownDataAPI, RegistrationAPI } from "../core/request";
import { BackgroundVideo } from "./Component/backgroundVideo";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchRegistrationDropdownDataAction } from "../../../redux/actions/auth.actions";
import SimpleLoader from "../../modules/apps/components/SimpleLoader";
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";

/* =========================
   Validation Schema
========================= */
const RegistrationSchema = Yup.object({
    org_type: Yup.string()
        .oneOf(["government", "public", "private"])
        .required("Organization type is required"),

    user_type: Yup.string().required("User type is required"),

    designation_data_key: Yup.string().required("Designation is required"),

    designation_specific_data_key: Yup.string().required(
        "Designation specific is required"
    ),

    department_id: Yup.string().required("Department is required"),

    first_name: Yup.string()
        .trim()
        .min(2)
        .max(50)
        .required("First name is required"),

    last_name: Yup.string()
        .trim()
        .min(2)
        .max(50)
        .required("Last name is required"),

    email: Yup.string()
        .trim()
        .lowercase()
        .email("Invalid email format")
        .required("Email is required"),

    phone_number: Yup.string()
        .matches(/^[0-9]{7,15}$/, "Phone number must be 7-15 digits")
        .required("Phone number is required"),
});

/* =========================
   Main Component
========================= */
const Registration: React.FC = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const { registrationDropdownData }: { registrationDropdownData: IRegistrationDropdownData } = useSelector((state: RootState) => state.authReducer);
    // Mock options - replace with API calls
    const orgTypeOptions: { label: string; value: string }[] = [
        { label: "Government", value: "government" },
        { label: "Public", value: "public" },
        { label: "Private", value: "private" },
    ]

    const handleFetchRegistrationDropdownData = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const response = await getRegistrationDropdownDataAPI(controller.signal);
            if (controller.signal.aborted) return;
            if (response.status === "ok") {
                dispatch(fetchRegistrationDropdownDataAction(response.data));
            } else {
                console.error("API Error:", response.message);
                showSnackbar(response.message || "Failed to fetch registration dropdown data", "danger");
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error("Fetch Registration Dropdown Data Error:", error)
            showSnackbar(getApiErrorMessage(error, "Failed to fetch registration dropdown data"), "danger");
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [dispatch, showSnackbar]);


    const mapOptions = (
        arr: any[],
        labelKey: string,
        valueKey: string
    ) =>
        arr?.map((item) => ({
            label: item[labelKey],
            value: item[valueKey],
        })) || [];

    const userTypeOptions = useMemo(
        () => mapOptions(registrationDropdownData?.userType, "display_name", "data_key"),
        [registrationDropdownData]
    );

    const designationOptions = useMemo(
        () =>
            mapOptions(
                registrationDropdownData?.userDesignations,
                "display_name",
                "data_key"
            ),
        [registrationDropdownData]
    );

    const designationSpecificOptions = useMemo(
        () =>
            mapOptions(
                registrationDropdownData?.userDesignationSpecifics,
                "display_name",
                "data_key"
            ),
        [registrationDropdownData]
    );

    const departmentOptions = useMemo(
        () =>
            mapOptions(registrationDropdownData?.departments, "department_name", "department_id"),
        [registrationDropdownData]
    );

    const handleSubmit = useCallback(async (values: IRegistrationForm) => {
        setIsSubmitting(true);
        try {
            const response = await RegistrationAPI(values);
            if (response.status === "ok") {
                showSnackbar(response.message, "success");
                // Auto login after registration or redirect to login
                navigate("/auth/login");
            } else {
                showSnackbar(response.message || "Registration failed. Please try again.", "danger");
            }
        } catch (error) {
            console.error("Registration error:", error);
            showSnackbar(getApiErrorMessage(error, "Registration failed. Please try again."), "danger");
        } finally {
            setIsSubmitting(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        if (!registrationDropdownData) {
            handleFetchRegistrationDropdownData();
        }
        return () => {
            abortRef.current?.abort();
        };
    }, [handleFetchRegistrationDropdownData]);

    return (
        <div className="position-relative vh-100 overflow-hidden">
            {loading && <SimpleLoader />}
            <PublicHeaderWrapper />
            <BackgroundVideo />

            <div className="d-flex flex-column justify-content-center align-items-center vh-100 position-relative">
                <div className="public-pages-container">
                    <div className="row px-2 px-xl-20 px-lg-0 align-items-center">
                        {/* LEFT SIDE */}
                        <div className="login-card col-12 col-md-6 pe-xl-20 px-sm-20 px-md-0 align-items-center d-flex flex-column mt-20" style={{ maxHeight: "80vh" }}>
                            <div className="w-100 gap-5 overflow-y-auto overflow-x-hidden">
                                <div className="mb-5">
                                    <p className="fw-bold fs-3 text-start mb-1">Create Account</p>
                                    <p className="fs-6 text-start m-0">Enter your details to register for the platform</p>
                                </div>

                                {/* FORMIK FORM */}
                                <Formik
                                    initialValues={{
                                        org_type: 'government',
                                        user_type: "",
                                        designation_data_key: "",
                                        designation_specific_data_key: "",
                                        department_id: "",
                                        first_name: "",
                                        last_name: "",
                                        email: "",
                                        phone_number: "",
                                    }}
                                    validationSchema={RegistrationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    {({ setFieldValue, values }) => (
                                        <Form className="w-100 d-flex flex-column">
                                            {/* Organization Type */}
                                            <div className="mb-4">
                                                <label className="form-label login-field-label mb-2">Organization Type:</label>
                                                <div className="d-flex justify-content-start gap-10 align-items-center text-center">
                                                    {orgTypeOptions.map((opt) => (
                                                        <div key={opt.value} className="d-flex gap-2">
                                                            <Field
                                                                className="form-check-input"
                                                                type="radio"
                                                                name="org_type"
                                                                disabled={opt.value !== 'government' || loading || isSubmitting}
                                                                value={opt.value}
                                                                checked={values.org_type === opt.value}
                                                                onChange={() => setFieldValue("org_type", opt.value)}
                                                            />
                                                            <p className="mb-0">{opt.label}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                                <ErrorMessage name="org_type" component="div" className="text-danger small mt-1" />
                                            </div>

                                            <div className="row">
                                                {/* User Type */}
                                                <div className="col-md-6 mb-4">
                                                    <label className="form-label login-field-label mb-1">User Type</label>
                                                    <CustomFormSearchSelect
                                                        options={userTypeOptions}
                                                        placeholder="Select User Type"
                                                        name="user_type"
                                                        value={values.user_type}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(value) => setFieldValue("user_type", value)}
                                                    />
                                                    <ErrorMessage name="user_type" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* Designation */}
                                                <div className="col-md-6 mb-4">
                                                    <label className="form-label login-field-label mb-1">Designation</label>
                                                    <CustomFormSearchSelect
                                                        options={designationOptions}
                                                        placeholder="Select Designation"
                                                        name="designation_data_key"
                                                        value={values.designation_data_key}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(value) => setFieldValue("designation_data_key", value)}
                                                    />
                                                    <ErrorMessage name="designation_data_key" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* Designation Specific */}
                                                <div className="col-md-6 mb-4">
                                                    <label className="form-label login-field-label mb-1">Designation Specific</label>
                                                    <CustomFormSearchSelect
                                                        options={designationSpecificOptions}
                                                        placeholder="Select Designation Specific"
                                                        name="designation_specific_data_key"
                                                        value={values.designation_specific_data_key}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(value) => setFieldValue("designation_specific_data_key", value)}
                                                    />
                                                    <ErrorMessage name="designation_specific_data_key" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* Department */}
                                                <div className="col-md-6 mb-4">
                                                    <label className="form-label login-field-label mb-1">Department</label>
                                                    <CustomFormSearchSelect
                                                        options={departmentOptions}
                                                        placeholder="Select Department"
                                                        name="department_id"
                                                        value={values.department_id}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(value) => setFieldValue("department_id", value)}
                                                    />
                                                    <ErrorMessage name="department_id" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* First Name */}
                                                <div className="col-md-6 mb-4">
                                                    <label className="form-label login-field-label mb-1">First Name</label>
                                                    <CustomFormInput
                                                        placeholder="Enter First Name"
                                                        name="first_name"
                                                        value={values.first_name}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(e) => setFieldValue("first_name", e.target.value)}
                                                    />
                                                    <ErrorMessage name="first_name" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* Last Name */}
                                                <div className="col-md-6 mb-4">
                                                    <label className="form-label login-field-label mb-1">Last Name</label>
                                                    <CustomFormInput
                                                        placeholder="Enter Last Name"
                                                        name="last_name"
                                                        value={values.last_name}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(e) => setFieldValue("last_name", e.target.value)}
                                                    />
                                                    <ErrorMessage name="last_name" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* Email */}
                                                <div className="col-md-12 mb-4">
                                                    <label className="form-label login-field-label mb-1">Email</label>
                                                    <CustomFormInput
                                                        placeholder="Enter Email"
                                                        name="email"
                                                        value={values.email}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(e) => setFieldValue("email", e.target.value)}
                                                    />
                                                    <ErrorMessage name="email" component="div" className="text-danger small mt-1" />
                                                </div>

                                                {/* Phone Number */}
                                                <div className="col-md-12 mb-4">
                                                    <label className="form-label login-field-label mb-1">Phone Number</label>
                                                    <CustomFormInput
                                                        placeholder="Enter Phone Number"
                                                        name="phone_number"
                                                        type="number"
                                                        value={values.phone_number}
                                                        disabled={loading || isSubmitting}
                                                        onChange={(e) => setFieldValue("phone_number", e.target.value)}
                                                    />
                                                    <ErrorMessage name="phone_number" component="div" className="text-danger small mt-1" />
                                                </div>
                                            </div>

                                            {/* Submit Button */}
                                            <button
                                                type="submit"
                                                className="rounded-pill login-btn w-100"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? "Registering..." : "Complete Register"}
                                            </button>

                                            <div className="mt-8 text-center">
                                                <p className="mb-0">Already have an account? <Link to="/auth/login" className="auth-link fw-bold">Login</Link></p>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="col-12 col-md-6 ps-lg-20 px-md-10 ps-0 d-none d-md-block">
                            <p className="login-title text-center">
                                One Platform. One Province. One Future.
                            </p>
                            <p className="text-center login-subtitle">
                                Step into the future with us and experience a vision of our 2050 infrastructure excellence.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Registration;